import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Server, CreditCard, FileText, Globe, LifeBuoy,
  Settings, LogOut, Monitor, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    ],
  },
  {
    label: 'Hosting',
    items: [
      { path: '/dashboard/services', label: 'My Services', icon: Server },
      { path: '/dashboard/cpanel', label: 'cPanel Access', icon: Monitor },
      { path: '/dashboard/domains', label: 'Domains & DNS', icon: Globe },
    ],
  },
  {
    label: 'Billing',
    items: [
      { path: '/dashboard/billing', label: 'Billing', icon: CreditCard },
      { path: '/dashboard/invoices', label: 'Invoices', icon: FileText },
    ],
  },
  {
    label: 'Support',
    items: [
      { path: '/dashboard/tickets', label: 'Support Tickets', icon: LifeBuoy },
    ],
  },
  {
    label: 'Account',
    items: [
      { path: '/dashboard/settings', label: 'Settings', icon: Settings },
    ],
  },
];

export default function DashboardSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const { signOut, customer } = useAuth();

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />
      )}

      <aside className={`fixed left-0 top-0 z-50 flex h-full w-64 flex-col border-r border-slate-200 bg-white transition-transform lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-5">
          <img src={`${process.env.PUBLIC_URL}/sightline-logo-white.png`} alt="SightLine Hosting" className="h-8 w-auto" />
          <span className="text-xs font-semibold text-sky-600">HOSTING</span>
        </div>

        {/* User info */}
        {customer && (
          <div className="border-b border-slate-100 px-5 py-3">
            <div className="text-sm font-medium text-slate-900">{customer.first_name} {customer.last_name}</div>
            <div className="text-xs text-slate-500 truncate">{customer.email}</div>
          </div>
        )}

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map(group => (
            <div key={group.label} className="mb-4">
              <div className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                {group.label}
              </div>
              {group.items.map(item => {
                const active = isActive(item.path, item.exact);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${active ? 'bg-sky-50 text-sky-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {active && <ChevronRight className="h-3 w-3" />}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Sign out */}
        <div className="border-t border-slate-200 p-3">
          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}
